import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { AlertColor, Box, Card, Container, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import Config from 'utils/Config';
import LanguageDrodownValue from 'utils/hooks/LanguageDrodownValue';
import {
  defaultLanguage,
  handleLanguageChange,
  parentOrgInitData,
  setParentOrgInitData,
  setSelectedLanguage,
  setTheme,
} from 'utils/redux/reducer/authentication-slice';
import { createDropdownData } from 'utils/validators/HelperFunctions';
import strings from '../../common/Translation/Translate';
import MultiSelect from '../../components/MultiSelect/MultiSelect';
import Notification from '../../components/Notification/Notification';
import axiosInstance from '../../utils/axios';
import { identifiers } from '../../utils/constants/identifiers';
import { URLS } from '../../utils/constants/urls';
import { fetchFromStorage } from '../../utils/storage';
import './Login/Login.scss';

export type AuthPageMessage = {
  display: boolean;
  severity: AlertColor | null;
  message: string;
};

type AuthPageShellProps = {
  title?: string;
  subtitle?: ReactNode;
  children: ReactNode;
  message?: AuthPageMessage;
  onCloseNotification?: (value: boolean) => void;
  /** When false, skips logo/title brand block (rare). Default true. */
  showBrand?: boolean;
};

/**
 * Shared layout for Login and related auth flows (forgot / reset / first-time / migration).
 * Matches Login: soft primary waves, centered card, logo slot, optional title.
 */
const AuthPageShell: FC<AuthPageShellProps> = ({
  title,
  subtitle,
  children,
  message,
  onCloseNotification,
  showBrand = true,
}) => {
  const { logoUrl } = useSelector(parentOrgInitData);
  const dispatch = useDispatch();
  const languageData = LanguageDrodownValue() as any[];
  const defaultLang = useSelector(defaultLanguage);
  const localDefaultLanguage = fetchFromStorage(identifiers.selectedLanguage);
  const language = defaultLang || localDefaultLanguage;
  const theme = useTheme();
  const screenSizeUpLg = useMediaQuery(theme.breakpoints.up('lg'));
  const screenSizeDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const [logo, setLogo] = useState<string>('');

  const logoSrc = useMemo(() => {
    const url = logo || logoUrl;
    return url ? Config.BaseURL + url : '';
  }, [logo, logoUrl]);

  const getTheme = useCallback(async () => {
    try {
      const { status, data } = await axiosInstance.get(URLS.getTheme);
      if (status === 200) {
        dispatch(setTheme({ ...data, logoBackgroundColor: data.logoBackgroundColor || '#A4E3F3' }));
      }
    } catch {
      // Theme is optional for auth pages; keep defaults.
    }
  }, [dispatch]);

  const getParentOrgInitialData = useCallback(async () => {
    try {
      const { status, data } = await axiosInstance.get(URLS.getParentOrgInitialData);
      if (status === 200) {
        dispatch(
          setParentOrgInitData({
            faviconUrl: data.favicon === null ? '' : data.favicon,
            title: data.title,
            logoUrl: data.logo,
            show24HourClock: data?.show24HourClock || false,
            msClarityId: data?.msClarityId || '',
            loginTypes: data?.loginTypes || [],
          }),
        );
        setLogo(data.logo);
      }
    } catch {
      // Logo falls back to placeholder text.
    }
  }, [dispatch]);

  useEffect(() => {
    void getTheme();
  }, [getTheme]);

  useEffect(() => {
    void getParentOrgInitialData();
  }, [getParentOrgInitialData]);

  useEffect(() => {
    dispatch(setSelectedLanguage(language));
  }, [dispatch, language, languageData]);

  const handleDropdownChange = async (value: any) => {
    try {
      dispatch(setSelectedLanguage(value));
      dispatch(handleLanguageChange(value) as any);
    } catch {
      // Language change failures are non-blocking on auth pages.
    }
  };

  return (
    <Container
      sx={{
        ...(screenSizeUpLg ? {} : { px: 0 }),
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        bgcolor: '#f3f5f8',
      }}
      className="login-container"
      maxWidth={false}>
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          pointerEvents: 'none',
        }}>
        <Box
          component="svg"
          viewBox="0 0 1440 900"
          preserveAspectRatio="xMidYMid slice"
          sx={{ width: '100%', height: '100%', display: 'block' }}>
          <path
            d="M0 0H1440V160C1180 220 980 80 720 140C460 200 240 60 0 110V0Z"
            fill={theme.palette.primary.main}
            fillOpacity={0.06}
          />
          <path
            d="M0 900V640C240 700 460 560 720 620C980 680 1200 560 1440 610V900H0Z"
            fill={theme.palette.primary.main}
            fillOpacity={0.08}
          />
        </Box>
      </Box>

      {languageData && languageData.length > 1 && (
        <Grid container justifyContent="flex-end" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid item xs={6} sm={5} md={4} lg={3} mr={1} mb={2}>
            <MultiSelect
              isClearable={false}
              placeholder={strings.language_dropdown_placeholder_text}
              field="language"
              className="languages"
              options={createDropdownData(languageData, ['value', 'text']) || []}
              onChange={handleDropdownChange}
              inputValue={language}
              id="language"
            />
          </Grid>
        </Grid>
      )}

      <Grid
        container
        justifyContent="center"
        alignItems="center"
        sx={{ minHeight: 'calc(100vh)', position: 'relative', zIndex: 1 }}>
        {message?.display && (
          <Notification
            isOpen={message.display}
            message={message.message}
            severity={message.severity as AlertColor}
            closeNotification={onCloseNotification || (() => undefined)}
          />
        )}
        <Grid item xs={12} sm={8} md={6} lg={4} xl={3.5}>
          <Card
            className="login-card"
            sx={{
              p: screenSizeDownSm ? 3 : 4.5,
              mx: screenSizeDownSm ? 2 : 0,
              borderRadius: '16px',
              boxShadow: '0 8px 30px rgba(15, 23, 42, 0.08)',
              border: '1px solid rgba(148, 163, 184, 0.15)',
            }}>
            {showBrand && (
              <Box className="login-brand" sx={{ textAlign: 'center', mb: title || subtitle ? 3 : 2 }}>
                <Box
                  className="login-logo-slot"
                  sx={{
                    width: '100%',
                    maxWidth: 240,
                    height: 100,
                    mx: 'auto',
                    mb: title || subtitle ? 2.5 : 0,
                    py: 1,
                    px: 2,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    overflow: 'hidden',
                    bgcolor: theme.palette.primary.light,
                    borderRadius: 1,
                  }}>
                  {logoSrc ? (
                    <Box
                      component="img"
                      src={logoSrc}
                      alt="logo"
                      sx={{
                        display: 'block',
                        width: 'auto',
                        height: 'auto',
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        objectPosition: 'center',
                      }}
                    />
                  ) : (
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: '1.125rem',
                        color: theme.palette.primary.main,
                        lineHeight: 1.2,
                        px: 1,
                      }}>
                      bookbanquetroom.com
                    </Typography>
                  )}
                </Box>
                {title ? (
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: '1.5rem', sm: '1.75rem' },
                      color: '#0f2744',
                      lineHeight: 1.2,
                      mb: subtitle ? 0.75 : 0,
                    }}>
                    {title}
                  </Typography>
                ) : null}
                {subtitle ? (
                  <Typography
                    sx={{
                      fontSize: '0.9375rem',
                      color: 'text.secondary',
                      lineHeight: 1.45,
                      px: 1,
                    }}>
                    {subtitle}
                  </Typography>
                ) : null}
              </Box>
            )}
            {children}
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default AuthPageShell;

/** Shared primary CTA style for auth forms (matches Login). */
export const authPrimaryButtonSx = {
  mt: 2,
  py: 1.25,
  fontSize: '1rem',
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '8px',
} as const;

/** Shared secondary link style under auth forms. */
export const authLinkSx = (primaryMain: string) => ({
  mt: 2,
  textAlign: 'center' as const,
  color: primaryMain,
  cursor: 'pointer',
  '&:hover': { textDecoration: 'underline' },
});
